import type { PropertyBlockData } from "@/types";
import { useTranslation } from "react-i18next";
import { useMapStore } from "@/stores/mapStore";

interface PropertyCardProps {
  data: PropertyBlockData;
}

function formatValue(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B kr`;
  if (value >= 1_000_000) return `${Math.round(value / 1_000_000)}M kr`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}k kr`;
  return `${value.toLocaleString()} kr`;
}

function formatArea(value: number): string {
  return Math.round(value).toLocaleString();
}

export function PropertyCard({ data }: PropertyCardProps) {
  const { i18n } = useTranslation();
  const isSv = i18n.language === "sv";

  // Build info chips (dot-separated, second row)
  const infoParts: string[] = [];
  if (data.area_sqm) infoParts.push(`${formatArea(data.area_sqm)} m²`);
  if (data.sublocality) infoParts.push(data.sublocality);
  else if (data.address) infoParts.push(data.address);
  if (data.taxation?.assessed_value) infoParts.push(formatValue(data.taxation.assessed_value));
  if (data.taxation?.year_built) infoParts.push(`${isSv ? "byggår" : "built"} ${data.taxation.year_built}`);

  const story = data.development_story;

  const handleShowBoundary = () => {
    useMapStore.getState().setFlyToTarget({
      entityId: data.property_id,
      entityType: "property",
      timestamp: Date.now(),
    });
  };

  return (
    <div className="cx-pc animate-cx-message-in">
      {/* Row 1: name + tag */}
      <div className="cx-pc-top">
        <div className="cx-pc-name">{data.display_name}</div>
        <div className="cx-pc-type">{data.property_type || "Fastighet"}</div>
      </div>

      {/* Row 2: info chips */}
      {infoParts.length > 0 && (
        <div className="cx-pc-info">
          {infoParts.map((p, i) => <span key={i}>{p}</span>)}
        </div>
      )}

      {/* Dev story */}
      {story && (
        <div className={`cx-pc-story${story.quiet ? " cx-pc-story--quiet" : ""}`}>
          {story.headline}
        </div>
      )}

      {/* Boundary link */}
      <button className="cx-pc-boundary" onClick={handleShowBoundary}>
        ⬡ {isSv ? "Visa fastighetsgräns" : "Show property boundary"}
      </button>
    </div>
  );
}
